import { BaseCartProduct, Cart } from '~shared-components/model'
import { CartState } from '~shared-components/redux/reducers/cart.reducers'
import { hashString } from '~shared-components/utils/functional'

export enum LocalStorage {
  'CART_STATE' = 'cartState',
  'SELECTED_CONTEXT' = 'selectedContext',
  'SELECTED_SALESGROUP_ID' = 'selectedSalesGroupId',
}

interface SelectedContextState {
  contexts: SelectedContext[]
  currentContext: SelectedContext
}

interface SelectedContext {
  selectedContextId: string
  selectedLocationId?: string
}

export interface SavedPrice {
  amount: number
  currency: string
  precision: number
}

export interface SavedCartProduct extends Omit<BaseCartProduct, 'price'> {
  price?: SavedPrice
}

export interface SavedCart
  extends Omit<Cart, 'billToLocation' | 'shipToLocation' | 'products'> {
  billToLocation?: string
  products: SavedCartProduct[]
  shipToLocation?: string
}

export interface SavedCartState extends Omit<CartState, 'carts'> {
  carts: SavedCart[]
}

export const saveToLocalStorage = <T>(
  userId: string,
  state: T,
  key: LocalStorage,
) => {
  try {
    const serializedState = JSON.stringify(state)
    localStorage.setItem(`${hashString(userId)}-${key}`, serializedState)
  } catch (error) {
    // no-op
    // eslint-disable-next-line no-console
    console.error(error)
  }
}

export const loadFromLocalStorage = <T>(
  userId: string,
  key: LocalStorage,
): T | undefined => {
  try {
    const serializedState = localStorage.getItem(`${hashString(userId)}-${key}`)
    return serializedState === null ? undefined : JSON.parse(serializedState)
  } catch (error) {
    return undefined
  }
}

export const loadSelectedContextState = (userId: string) =>
  loadFromLocalStorage<SelectedContextState>(
    userId,
    LocalStorage.SELECTED_CONTEXT,
  )

export const loadContextFromStorage = (userId: string, contextId: string) =>
  loadSelectedContextState(userId)?.contexts.find(
    c => c.selectedContextId === contextId,
  )

export const saveSelectedSalesGroupId = (
  userId: string | undefined,
  salesGroupId: string,
) =>
  userId
    ? saveToLocalStorage(
        userId,
        salesGroupId,
        LocalStorage.SELECTED_SALESGROUP_ID,
      )
    : undefined

export const loadSelectedSalesGroupId = (userId?: string) =>
  userId
    ? loadFromLocalStorage<string>(userId, LocalStorage.SELECTED_SALESGROUP_ID)
    : undefined

export const saveSelectedContextState = (
  userId: string,
  state: SelectedContext,
) => {
  const storedContexts = loadSelectedContextState(userId)

  const contexts = [
    ...(storedContexts?.contexts ?? []).filter(
      c => c.selectedContextId !== state.selectedContextId,
    ),
    state,
  ]

  saveToLocalStorage<SelectedContextState>(
    userId,
    {
      contexts,
      currentContext: state,
    },
    LocalStorage.SELECTED_CONTEXT,
  )
}

export const saveCartState = (userId: string, state: CartState) => {
  const savedCartState: SavedCartState = {
    ...state,
    carts: state.carts.map(cart => ({
      ...cart,
      billToLocation: cart.billToLocation?.id,
      products: cart.products.map(p => ({
        ...p,
        price: p.price ? p.price.toObject() : undefined,
      })),
      shipToLocation: cart.shipToLocation?.id,
    })),
  }

  saveToLocalStorage<SavedCartState>(
    userId,
    savedCartState,
    LocalStorage.CART_STATE,
  )
}
export const loadCartState = (userId: string) =>
  loadFromLocalStorage<SavedCartState>(userId, LocalStorage.CART_STATE)
